import * as React from 'react';
import { Box, MenuItem, Paper, TextField, TextFieldProps } from '@material-ui/core';
import { fetchAddressDetails, searchAddresses } from '../helpers/apiCalls';
import { Autocomplete } from '@material-ui/lab';
import { v4 as uuidv4 } from 'uuid';
import { IAddress, IAddressDetails } from '../types/companyDetails';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

type OwnProps = {
  title: string;
  value: string;
  isRequired: boolean;
  errors: any[];
  name: string;
  getCountry: () => string;
  onChange: (address: string) => void;
  showDetails: () => void;
  setAddressDetails: (details: IAddressDetails) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
};
type Props = Omit<TextFieldProps, 'value' | 'onChange' | 'onBlur'> & OwnProps;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    option: {
      textDecoration: 'underline',
      textDecorationThickness: 'from-font',
      '& .MuiButtonBase-root': {
        fontWeight: 500,
        fontSize: '0.75rem',
        lineHeight: '1rem',
        letterSpacing: '0.025em',
        paddingBottom: theme.spacing(1.25),
        paddingTop: theme.spacing(1.25),
        '&:hover': {
          backgroundColor: 'var(--list-hover)'
        }
      }
    },
    autocomplete: {
      backgroundColor: 'var(--medium-gray)',
      fontSize: '0.875rem',
      lineHeight: '1.25rem',
      letterSpacing: '0.015625em',
      borderRadius: '4px',
      '& .MuiAutocomplete-option[data-focus="true"], .MuiAutocomplete-option:active': {
        backgroundColor: 'var(--list-hover)'
      },
      '& .MuiAutocomplete-listbox': {
        paddingBottom: 0
      },
      '& .MuiAutocomplete-option': {
        paddingBottom: theme.spacing(1),
        paddingTop: theme.spacing(1),
        '&:hover': {
          backgroundColor: 'var(--list-hover)'
        }
      }
    }
  })
);

export const AddressContainer: React.FC<Props> = props => {
  const classes = useStyles();
  const { t } = useTranslation('signup');
  const [open, setOpen] = React.useState<boolean>(false);
  const [addresses, setAddresses] = React.useState<IAddress[]>([]);
  const [isAddressesLoading, setIsAddressesLoading] = React.useState<boolean>(false);
  const sessionIdRef = React.useRef<string>('');
  const manualOption = t('fields.manual', 'Enter full address manually');

  const { value, title, onChange, getCountry, isRequired, errors, disabled, setAddressDetails, showDetails } = props;
  const error = errors.length > 0;
  const options = addresses.map(({ address }) => address);

  const searchAddressesByText = React.useCallback(
    debounce(async (search: string) => {
      try {
        if (!sessionIdRef.current) {
          sessionIdRef.current = uuidv4();
        }
        setIsAddressesLoading(true);
        const response = await searchAddresses(search, getCountry(), sessionIdRef.current);
        setAddresses(response.data);
      } catch (error) {
        throw error;
      } finally {
        setIsAddressesLoading(false);
      }
    }, 500),
    []
  );

  const fetchDetails = async (placeId: string) => {
    try {
      const response = await fetchAddressDetails(placeId, sessionIdRef.current);
      setAddressDetails(response.data);
    } catch (error) {
      throw error;
    }
  };

  const handleInputChange = (_: React.ChangeEvent<HTMLInputElement>, newValue: string, reason: string) => {
    if (newValue !== value && newValue !== manualOption && reason !== 'reset') {
      onChange(newValue);
      if (newValue) {
        searchAddressesByText(newValue);
        setOpen(true);
      } else {
        setOpen(false);
      }
    }
  };

  const handleSelect = async (_: React.ChangeEvent<HTMLInputElement>, value: string, reason: string) => {
    if (reason === 'clear') {
      setAddresses([]);
    }
    if (value !== manualOption) {
      const address = addresses.find(option => option.address === value);
      if (address) {
        await fetchDetails(address.id);
        sessionIdRef.current = uuidv4();
        setOpen(false);
      }
    } else {
      showDetails();
    }
  };

  const handleManualOptionSelect = () => {
    setOpen(false);
    showDetails();
  };

  const handleBlur = () => {
    if (value.length) {
      handleManualOptionSelect();
    }
  };

  function CustomPaper({ children, ...other }: React.HTMLAttributes<HTMLElement>) {
    return (
      <Paper {...other}>
        {children}
        <Box
          className={classes.option}
          onMouseDown={event => {
            event.preventDefault();
          }}
        >
          <MenuItem onClick={handleManualOptionSelect} value={manualOption}>
            {manualOption}
          </MenuItem>
        </Box>
      </Paper>
    );
  }

  return (
    <Autocomplete
      forcePopupIcon={false}
      open={open}
      disabled={disabled}
      onClose={() => setOpen(false)}
      loading={isAddressesLoading}
      value={value || ''}
      inputValue={value || ''}
      noOptionsText={t('fields.noResults', 'No results found')}
      loadingText={t('form.autoComplete.loading', 'Loading...')}
      options={options}
      filterOptions={options => options}
      getOptionLabel={option => option}
      onChange={handleSelect}
      onInputChange={handleInputChange}
      PaperComponent={CustomPaper}
      classes={{ paper: classes.autocomplete }}
      onBlur={handleBlur}
      renderInput={params => (
        <TextField
          {...params}
          label={title}
          inputProps={{ ...params.inputProps, tabIndex: 1 }}
          variant="outlined"
          required={isRequired}
          fullWidth
          error={error}
          helperText={error && errors[0].locText.text}
          onBlur={props.onBlur}
        />
      )}
      freeSolo
    />
  );
};
